import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid } from "lucide-react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { cn } from "@/lib/utils";

/** Máximo de módulos como icono directo; si el usuario tiene más, el resto va a «Más». */
const PILL_MAX = 5;
/** Prioridad para ocupar la píldora; los demás siguen el orden del menú. */
const PILL_PRIORITY = ["/chat", "/candidatos", "/gmb", "/revisiones", "/trabajos", "/metricas", "/formularios"];

function CountBadge({ count, className }) {
  if (!count) return null;
  return (
    <span
      className={cn(
        "absolute flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-medium px-1",
        className
      )}
    >
      {count}
    </span>
  );
}

/**
 * Navegación móvil (< md): píldora flotante abajo, solo iconos.
 * Recibe los mismos items (ya filtrados por permisos) que la nav de escritorio de AppMainHeader.
 * El hueco que necesita se reserva en index.css (body:has(.mobile-nav-pill)).
 */
export function MobileNavPill({ items }) {
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const rank = (item) => {
    const i = PILL_PRIORITY.indexOf(item.path);
    return i === -1 ? PILL_PRIORITY.length + items.indexOf(item) : i;
  };
  const ordered = [...items].sort((a, b) => rank(a) - rank(b));
  const pillItems = ordered.slice(0, PILL_MAX);
  const moreItems = ordered.slice(PILL_MAX);
  const moreActive = moreItems.some((i) => i.active);
  const moreHasBadge = moreItems.some((i) => i.badge > 0);

  const go = (path) => {
    setMoreOpen(false);
    navigate(path);
  };

  const iconButtonClass = (active) =>
    cn(
      "relative flex h-11 w-11 touch-manipulation items-center justify-center rounded-full transition-colors",
      active ? "bg-foreground text-background" : "text-muted-foreground active:bg-muted"
    );

  return (
    <>
      {/* Banda difuminada detrás de la píldora: el contenido pasa por debajo y se desenfoca, desvaneciéndose hacia arriba */}
      <div
        aria-hidden
        className="mobile-nav-band md:hidden fixed inset-x-0 bottom-0 z-30 h-[calc(env(safe-area-inset-bottom)+88px)] pointer-events-none backdrop-blur-md bg-gradient-to-t from-background/70 to-background/0 [mask-image:linear-gradient(to_top,black_55%,transparent)] [-webkit-mask-image:linear-gradient(to_top,black_55%,transparent)]"
      />
      <nav
        aria-label="Navegación principal"
        className="mobile-nav-pill md:hidden fixed left-1/2 -translate-x-1/2 z-40 bottom-[calc(env(safe-area-inset-bottom)+12px)] flex items-center gap-1 rounded-full border bg-background/85 backdrop-blur-md shadow-lg p-1.5"
      >
        {pillItems.map(({ path, label, icon: Icon, active, badge }) => (
          <button
            key={path}
            type="button"
            aria-label={label}
            aria-current={active ? "page" : undefined}
            onClick={() => go(path)}
            className={iconButtonClass(active)}
          >
            <Icon className="h-5 w-5" strokeWidth={1.75} />
            <CountBadge count={badge} className="top-0.5 right-0.5" />
          </button>
        ))}
        {moreItems.length > 0 && (
          <button
            type="button"
            aria-label="Más"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen(true)}
            className={iconButtonClass(moreActive)}
          >
            <LayoutGrid className="h-5 w-5" strokeWidth={1.75} />
            {moreHasBadge && <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-destructive" />}
          </button>
        )}
      </nav>

      <Drawer open={moreOpen} onOpenChange={setMoreOpen} shouldScaleBackground={false}>
        <DrawerContent className="pb-[calc(env(safe-area-inset-bottom)+16px)]">
          <DrawerHeader>
            <DrawerTitle>Más</DrawerTitle>
          </DrawerHeader>
          <div className="grid grid-cols-3 gap-2 px-4">
            {moreItems.map(({ path, label, icon: Icon, active, badge }) => (
              <button
                key={path}
                type="button"
                onClick={() => go(path)}
                className={cn(
                  "relative flex flex-col items-center justify-center gap-1.5 rounded-xl border py-4 text-xs font-medium",
                  active ? "bg-foreground text-background border-foreground" : "active:bg-muted"
                )}
              >
                <Icon className="h-5 w-5" strokeWidth={1.75} />
                {label}
                <CountBadge count={badge} className="top-1.5 right-1.5" />
              </button>
            ))}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
